import { Link } from "react-router-dom";
import { HERO } from "@/constants/testIds/clinic";
import { CLINIC } from "@/constants/clinic";

export default function Hero() {
    return (
        <section
            data-testid={HERO.section}
            className="relative isolate overflow-hidden ihx-warm-surface ihx-grid-overlay"
        >
            <div className="ihx-container relative pt-16 lg:pt-28 pb-12 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                {/* Left — editorial headline */}
                <div className="lg:col-span-7 relative z-10">
                    <div
                        data-testid={HERO.eyebrow}
                        className="ihx-eyebrow ihx-anim-fade-up"
                    >
                        Lifestyle &amp; Metabolic Medicine
                    </div>

                    <h1
                        data-testid={HERO.headline}
                        className="ihx-h-display mt-6 ihx-anim-fade-up ihx-delay-1"
                    >
                        Medicine that doesn’t{" "}
                        <br className="hidden sm:block" />
                        just <span className="ihx-italic">treat.</span>
                        <br />
                        Care that <span className="ihx-italic">transforms.</span>
                    </h1>

                    <p
                        data-testid={HERO.subheadline}
                        className="ihx-lead mt-8 max-w-xl ihx-anim-fade-up ihx-delay-3"
                    >
                        Welcome to a personalised, hybrid healthcare experience led by{" "}
                        <span className="text-[hsl(var(--brand-ink-deep))] font-medium">
                            {CLINIC.doctor.fullName}
                        </span>{" "}
                        (
                        <abbr title="Bachelor of Medicine, Bachelor of Surgery">MBBS</abbr>,{" "}
                        <abbr title="Doctor of Medicine">MD</abbr>,{" "}
                        <abbr title="International Board of Lifestyle Medicine">IBLM</abbr>
                        ). We merge clinical rigour with lifestyle science to reverse chronic
                        conditions and engineer long-term metabolic health.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4 ihx-anim-fade-up ihx-delay-4">
                        <Link
                            to="/apply"
                            data-testid={HERO.primaryCta}
                            className="ihx-btn-primary"
                        >
                            Apply for Consultation
                            <span className="ihx-arrow" aria-hidden>→</span>
                        </Link>
                        <Link
                            to="/approach"
                            data-testid={HERO.secondaryCta}
                            className="ihx-btn-secondary"
                        >
                            Explore the approach
                            <span aria-hidden>→</span>
                        </Link>
                    </div>

                    <div className="mt-12 lg:mt-14 grid grid-cols-3 gap-4 sm:gap-x-10 max-w-xl ihx-anim-fade-up ihx-delay-5">
                        {[
                            { v: "12+", l: "Years in practice" },
                            { v: "1,400+", l: "Personalised baselines" },
                            { v: "78%", l: "Patients reversing key markers*" },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="font-serif text-[24px] sm:text-[28px] text-[hsl(var(--brand-ink-deep))] leading-none">
                                    {s.v}
                                </div>
                                <div className="mt-2 text-[10.5px] sm:text-[11.5px] text-[hsl(var(--ink-muted))] tracking-wide leading-snug">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right — photo + floating intelligence cards */}
                <div className="lg:col-span-5 relative">
                    <div className="relative max-w-[460px] mx-auto lg:mx-0 lg:ml-auto">
                        {/* Photo */}
                        <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] bg-[hsl(var(--bg-stone))]">
                            <img
                                src={CLINIC.doctor.photo}
                                alt={`${CLINIC.doctor.fullName} — ${CLINIC.doctor.credentials}`}
                                className="w-full h-full object-cover object-top"
                                loading="eager"
                            />
                            {/* subtle warm overlay */}
                            <div
                                aria-hidden
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, transparent 55%, hsl(var(--brand-ink-deep) / 0.18) 100%)",
                                }}
                            />
                        </div>

                        {/* Tiny floating credential tag */}
                        <div
                            className="ihx-glass-card ihx-anim-fade-up ihx-delay-5 absolute left-4 -bottom-6 px-4 py-2 flex items-center gap-3"
                            style={{ borderRadius: 999 }}
                        >
                            <span className="w-2 h-2 rounded-full bg-[hsl(var(--brand-gold))]" />
                            <span className="text-[11px] tracking-[0.18em] uppercase font-medium text-[hsl(var(--brand-ink-deep))]">
                                MBBS · MD · IBLM
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <p className="ihx-container relative pb-8 lg:pb-12 pt-2 text-[10.5px] text-[hsl(var(--ink-muted))] tracking-wide">
                *Indicative of an internal cohort following the 12-week protocol.
            </p>
        </section>
    );
}
